import React, { useState, useEffect } from 'react';
import { endpoints } from '../../api/endpoints';
import { useModalKeyboardHandlers } from '../useModalKeyboardHandlers';

/**
 * ReviewImage Component
 * 
 * Displays a thumbnail grid of images.
 * Clicking an image opens a Modal to review all images in detail, 
 * styled similarly to the UploadImageModal.
 * 
 * @param {Object[]} images - Array of image objects { id, imageType, imageData, imageDefinition ... }
 */
const ReviewImage = ({ images, compact = false }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    if (!images || images.length === 0) {
        if (compact) {
            return <div className="text-muted text-center" style={{ fontSize: '0.9rem', color: '#adb5bd' }}>-</div>;
        }
        return null;
    }

    const handleOpen = () => setIsModalOpen(true);
    const handleClose = () => setIsModalOpen(false);

    return (
        <React.Fragment>
            {compact ? (
                /* Compact Trigger (for tables) */
                <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={(e) => { e.stopPropagation(); handleOpen(); }}
                    title={`View ${images.length} Images`}
                    style={{ padding: '0.1rem 0.4rem', fontSize: '0.8rem' }}
                >
                    <i className="bi bi-images me-1"></i> {images.length}
                </button>
            ) : (
                /* Thumbnail Grid Trigger */
                <div className="review-image-grid" style={{ marginTop: '1rem' }}>
                    <h6 style={{ marginBottom: '1rem', color: '#002244', display: 'flex', alignItems: 'center' }}>
                        <i className="bi bi-images me-2"></i> Experiment Images
                        <span className="badge bg-secondary ms-2" style={{ fontSize: '0.7em' }}>{images.length}</span>
                    </h6>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.5rem' }}>
                        {images.map((img, idx) => (
                            <div
                                key={img.id || idx}
                                onClick={handleOpen}
                                style={{
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s',
                                    height: '120px',
                                    background: '#f8f9fa',
                                    position: 'relative'
                                }}
                                className="image-thumbnail-card"
                                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                <img
                                    src={img.id ? endpoints.getImageContent(img.id) : (img.imageData ? `data:${img.imageType || 'image/png'};base64,${img.imageData}` : '')}
                                    alt={`Thumb ${idx}`}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = 'https://via.placeholder.com/150x120?text=Error';
                                    }}
                                />
                                <div style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    background: 'rgba(0,0,0,0.6)',
                                    color: '#fff',
                                    padding: '2px 5px',
                                    fontSize: '0.7rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>
                                    {img.imageType || 'Image'}
                                </div>
                            </div>
                        ))}
                    </div>
                    <button className="btn btn-sm btn-outline-primary mt-2" onClick={handleOpen}>
                        <i className="bi bi-arrows-fullscreen me-1"></i> Review All Images
                    </button>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && <ReviewImageModal images={images} onClose={handleClose} />}
        </React.Fragment>
    );
};

// Internal Modal Component
const ReviewImageModal = ({ images, onClose }) => {
    useModalKeyboardHandlers({ onClose });

    // Helper to format extra fields key
    const formatKey = (key) => key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }} role="dialog">
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
                <div className="modal-content shadow-lg border-0">
                    <div className="modal-header bg-light">
                        <h5 className="modal-title fw-bold text-dark">
                            <i className="bi bi-images me-2 text-primary"></i>
                            Review Images
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>

                    <div className="modal-body p-4 bg-light-subtle">
                        {images.map((img, idx) => (
                            <div key={img.id || idx} className="card border-light shadow-sm mb-4">
                                <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
                                    <h6 className="fw-bold text-secondary mb-0">Image #{idx + 1}: {img.imageType}</h6>
                                    {img.id && (
                                        <a
                                            href={endpoints.getImageContent(img.id)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-sm btn-outline-secondary"
                                            title="Open Original in New Tab"
                                        >
                                            <i className="bi bi-box-arrow-up-right"></i> Open Original
                                        </a>
                                    )}
                                </div>
                                <div className="card-body p-3">
                                    {/* Definitions / Extra Fields */}
                                    {img.imageDefinition && Object.keys(img.imageDefinition).length > 0 && (
                                        <div className="row g-3 mb-3">
                                            {Object.entries(img.imageDefinition).map(([key, value]) => (
                                                <div key={key} className="col-md-6">
                                                    <label className="form-label fw-bold text-secondary" style={{ fontSize: '0.85rem' }}>{formatKey(key)}</label>
                                                    <div className="form-control-plaintext p-2 bg-light rounded border-0" style={{ fontSize: '0.9rem' }}>
                                                        {value}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Image Display */}
                                    <div className="text-center bg-light rounded p-3 border" style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <img
                                            src={img.id ? endpoints.getImageContent(img.id) : (img.imageData ? `data:${img.imageType || 'image/png'};base64,${img.imageData}` : '')}
                                            alt={`Preview ${idx}`}
                                            className="img-fluid rounded shadow-sm"
                                            style={{ maxHeight: '500px', maxWidth: '100%' }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="modal-footer bg-light">
                        <button type="button" className="btn btn-secondary px-4" onClick={onClose}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReviewImage;
